import { FC, useState } from "react";
import { useFormContext, useFieldArray, useWatch } from "react-hook-form";
import { CardContainer } from "@/components/commons/CardContainer";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { Textarea } from "@/components/elements/Textarea";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { useDialog } from "@/hooks/useDialog";
import { IconAddCircle } from "@/assets/icons";
import { IconClose } from "@/assets/icons/IconClose";
import { TExamCreateForm } from "@/features/learning-content/types/learningContent";
import { EXAM_PROCEDURE_AGENT_OPTIONS } from "@/features/learning-content/constants/examOptions";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { button, text } from "@/theme/colors";

export const ProceduresForm: FC = () => {
  const { control, getValues } = useFormContext<TExamCreateForm>();
  const { fields, append, remove } = useFieldArray({ control, name: "procedures" });
  const examMethod = useWatch({ control, name: "examMethod" });
  const isCBT = examMethod === "cbt";
  const isPT = examMethod === "paper_test";
  const canAddMore = fields.length < 10;
  const confirmRemove = useDialog();
  const [removingHasAssignedLearners, setRemovingHasAssignedLearners] = useState(false);

  const hasData = (index: number): boolean => {
    const procedure = getValues(`procedures.${index}`);
    return !!(
      procedure?.agent ||
      procedure?.site ||
      procedure?.stepName ||
      procedure?.description ||
      (!isPT && procedure?.duration) ||
      (!isCBT && procedure?.deadline)
    );
  };

  const handleRemove = async (index: number) => {
    const hasAssignedLearners = !!getValues(`procedures.${index}`)?.hasAssignedLearners;
    if (hasData(index) || hasAssignedLearners) {
      setRemovingHasAssignedLearners(hasAssignedLearners);
      const confirmed = await confirmRemove.show();
      if (!confirmed) return;
    }
    remove(index);
  };

  return (
    <Stack gap={3}>
      <CardContainer sx={{ borderRadius: "8px" }}>
        <Stack gap={3}>
          <Typography variant="sub16Bold" sx={{ fontSize: "20px", fontWeight: 700 }}>
            受験手続き情報
          </Typography>

          {fields.map((field, index) => {
            const isLast = index === fields.length - 1;

            return (
              <Stack gap={1} key={field.id}>
                <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: "8px", p: 3 }}>
                  <Stack gap={2}>
                    <Typography variant="body14" sx={{ fontWeight: 700 }}>
                      手続き {index + 1}
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <FormField control={control} name={`procedures.${index}.agent`} label="手続きする人" required>
                          <Select options={EXAM_PROCEDURE_AGENT_OPTIONS} />
                        </FormField>
                      </Grid>
                      <Grid item xs={6}>
                        <FormField control={control} name={`procedures.${index}.site`} label="手続き先URL">
                          <Input fullWidth placeholder="入力してください" />
                        </FormField>
                      </Grid>
                    </Grid>

                    <FormField control={control} name={`procedures.${index}.stepName`} label="手続き表示名" required>
                      <Input fullWidth placeholder="入力してください" />
                    </FormField>

                    <FormField control={control} name={`procedures.${index}.description`} label="手続き内容の詳細説明">
                      <Textarea placeholder="入力してください" />
                    </FormField>

                    {/* 手続き期間（目安）: hide when PT */}
                    {!isPT && (
                      <FormField control={control} name={`procedures.${index}.duration`} label="手続き期間（目安）">
                        <Input fullWidth placeholder="入力してください" />
                      </FormField>
                    )}

                    {/* 期日: hide when CBT */}
                    {!isCBT && (
                      <FormField control={control} name={`procedures.${index}.deadline`} label="期日">
                        <InputDatePicker />
                      </FormField>
                    )}
                  </Stack>
                </Box>

                <Stack direction="row" alignItems="center" gap={2}>
                  {isLast && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={0.5}
                      onClick={
                        canAddMore
                          ? () => append({ agent: "", site: null, stepName: "", description: null, duration: null, deadline: null })
                          : undefined
                      }
                      sx={{
                        cursor: canAddMore ? "pointer" : "default",
                        color: text.primary,
                        opacity: canAddMore ? 1 : 0.4,
                        pointerEvents: canAddMore ? "auto" : "none",
                        width: "fit-content",
                      }}
                    >
                      <IconAddCircle fontSize={20} color={button.primary} />
                      <Typography variant="cap12">手続きを追加</Typography>
                    </Stack>
                  )}

                  {fields.length > 1 && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={0.5}
                      onClick={() => handleRemove(index)}
                      sx={{ cursor: "pointer", color: text.primary, width: "fit-content" }}
                    >
                      <IconClose fontSize={20} />
                      <Typography variant="cap12">削除</Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            );
          })}

          {fields.length === 0 && (
            <Stack
              direction="row"
              alignItems="center"
              gap={0.5}
              onClick={() => append({ agent: "", site: null, stepName: "", description: null, duration: null, deadline: null })}
              sx={{ cursor: "pointer", color: text.primary, width: "fit-content" }}
            >
              <IconAddCircle fontSize={20} color={button.primary} />
              <Typography variant="cap12">手続きを追加</Typography>
            </Stack>
          )}
        </Stack>
      </CardContainer>

      {/* 試験実施 section */}
      <CardContainer sx={{ borderRadius: "8px" }}>
        <Stack gap={3}>
          <Typography variant="sub16Bold" sx={{ fontSize: "20px", fontWeight: 700 }}>
            試験実施
          </Typography>

          <FormField control={control} name="examDayDescription" label="試験日の詳細説明">
            <Textarea placeholder="入力してください" />
          </FormField>
        </Stack>
      </CardContainer>

      {/* 合否判定 section */}
      <CardContainer sx={{ borderRadius: "8px" }}>
        <Stack gap={3}>
          <Typography variant="sub16Bold" sx={{ fontSize: "20px", fontWeight: 700 }}>
            合否判定
          </Typography>

          <FormField control={control} name="passFailDescription" label="合否判定の詳細説明">
            <Textarea placeholder="入力してください" />
          </FormField>

          <FormField control={control} name="announcementEstimate" label="発表目安">
            <Input fullWidth placeholder="入力してください" />
          </FormField>
        </Stack>
      </CardContainer>
      <ConfirmSaveModal title="削除の確認" open={confirmRemove.open} onOk={confirmRemove.confirm} onClose={confirmRemove.cancel}>
        <Typography component="p" variant="subtitle1">
          {removingHasAssignedLearners
            ? "該当の手続きステータスに設定されている外国人がいますが、削除してもよろしいですか？"
            : "「手続き」を削除してもよろしいですか？"}
        </Typography>
      </ConfirmSaveModal>
    </Stack>
  );
};
